import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, Typography, Tab } from '@mui/material';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import UserMemberInfo from './UserMemberInfo';
import UserPI from './UserPI';
import UserProjects from './UserProjects';
import UserPermissions from './UserPermissions';
import UserHistory from './UserHistory';
import UserNotes from './UserNotes';
import { useApiGet } from '../../utils/httpClient';
import { getUserDetail } from '../../endpoints/users';
import { roles } from '../../utils/variables';

const UserDetail = () => {
  let { id } = useParams();
  const [tabSelected, setTabSelected] = useState('0');
  const [needToRefetch, setNeedToRefetch] = useState(false);

  const { data, isFetching, refetch } = useApiGet(
    ['user-detail', id],
    getUserDetail,
    {
      enabled: true,
    },
    {
      id: id,
    }
  );

  const canSeeNotes = () => {
    let noteRoles = [roles.Admin, roles.Support];
    let usersRole = localStorage.getItem('role');
    return noteRoles.includes(usersRole);
  };

  const canSeePermissions = () => {
    let permissionRoles = [roles.Admin, roles.Support];
    let usersRole = localStorage.getItem('role');
    return permissionRoles.includes(usersRole);
  };

  const canSeeHistory = () => {
    let historyRoles = [roles.Admin, roles.Support];
    let usersRole = localStorage.getItem('role');
    return historyRoles.includes(usersRole);
  };

  const canSeeMemberInfo = () => {
    let memberInfoRoles = [
      roles.Admin,
      roles.Support,
      roles.SPDOfficer,
      roles.LCOfficer,
    ];
    let usersRole = localStorage.getItem('role');
    return memberInfoRoles.includes(usersRole) || data?.full_view;
  };

  if (isFetching) {
    return (
      <Grid container>
        <Grid item xs={12} padding textAlign={'center'}>
          <Typography variant="h4">Loading...</Typography>
        </Grid>
      </Grid>
    );
  }
  if (!data) {
    return (
      <Grid container>
        <Grid item xs={12} padding textAlign={'center'}>
          <Typography variant="h4">User not found</Typography>
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid container>
      <Grid item xs={12} padding textAlign={'center'}>
        <Typography variant="h4">{data?.full_name}</Typography>
      </Grid>
      <Grid item xs={12} padding>
        <TabContext value={tabSelected}>
          <TabList
            value={tabSelected}
            onChange={(event, newValue) => setTabSelected(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Info" value={'0'} />
            {canSeeMemberInfo() && (
              <Tab label="Member Information" value={'1'} />
            )}
            {data?.has_project_history && <Tab label="Projects" value={'2'} />}
            {canSeePermissions() && <Tab label="Permissions" value={'3'} />}
            {canSeeNotes() && <Tab label="Notes" value={'4'} />}
            {canSeeHistory() && <Tab label="History" value={'5'} />}
          </TabList>
          <TabPanel value={'0'}>
            <UserPI
              id={id}
              refetchParent={refetch}
              canEdit={data?.can_edit}
              canAction={data?.can_action}
              tabSelected={tabSelected}
              needToRefetch={needToRefetch}
              updateNeedToRefetch={setNeedToRefetch}
            />
          </TabPanel>
          {canSeeMemberInfo() && (
            <TabPanel value={'1'}>
              <UserMemberInfo
                id={id}
                canAction={data?.can_action}
                refetchParent={refetch}
                needToRefetch={needToRefetch}
                updateNeedToRefetch={setNeedToRefetch}
              />
            </TabPanel>
          )}
          <TabPanel value={'2'}>
            {data?.has_project_history && <UserProjects id={id} />}
          </TabPanel>
          <TabPanel value={'3'}>
            {canSeePermissions() && <UserPermissions id={id} />}
          </TabPanel>
          <TabPanel value={'4'}>
            {canSeeNotes() && <UserNotes id={id} />}
          </TabPanel>
          <TabPanel value={'5'}>
            {canSeeHistory() && <UserHistory id={id} />}
          </TabPanel>
        </TabContext>
      </Grid>
    </Grid>
  );
};

export default UserDetail;
